export type JsonHighlightKind = 'plain' | 'key' | 'string' | 'required';

export interface JsonHighlightSegment {
  kind: JsonHighlightKind;
  text: string;
}

export const JSON_HIGHLIGHT_COLORS: Record<Exclude<JsonHighlightKind, 'plain'>, string> = {
  key: '#7dd3fc',
  string: '#86efac',
  required: '#f87171',
};

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const KEY_PREFIX = /^("(?:\\.|[^"\\])*")(?=\s*:)/;
const STRING_PREFIX = /^:(\s*)("(?:\\.|[^"\\])*")/;
const STRING_LITERAL = /^("(?:\\.|[^"\\])*")/;
const REQUIRED_VALUE = '"[required]"';

function stringLiteralKind(value: string): JsonHighlightKind {
  return value === REQUIRED_VALUE ? 'required' : 'string';
}

function matchHighlightAt(text: string, from: number): { length: number; segments: JsonHighlightSegment[] } | null {
  const rest = text.slice(from);

  const key = KEY_PREFIX.exec(rest);
  if (key) {
    return {
      length: key[1].length,
      segments: [{ kind: 'key', text: key[1] }],
    };
  }

  const str = STRING_PREFIX.exec(rest);
  if (str) {
    const value = str[2];
    return {
      length: str[0].length,
      segments: [
        { kind: 'plain', text: `:${str[1]}` },
        { kind: stringLiteralKind(value), text: value },
      ],
    };
  }

  const literal = STRING_LITERAL.exec(rest);
  if (literal && !/^\s*:/.test(rest.slice(literal[1].length))) {
    const value = literal[1];
    return {
      length: literal[1].length,
      segments: [{ kind: stringLiteralKind(value), text: value }],
    };
  }

  return null;
}

/** Tokenizes JSON preview text for React syntax highlighting (keys, strings, [required]). */
export function parseJsonHighlights(json: string): JsonHighlightSegment[] {
  const text = escapeHtml(json);
  const segments: JsonHighlightSegment[] = [];
  let cursor = 0;
  let plainBuffer = '';

  const flushPlain = () => {
    if (!plainBuffer) return;
    segments.push({ kind: 'plain', text: plainBuffer });
    plainBuffer = '';
  };

  while (cursor < text.length) {
    const hit = matchHighlightAt(text, cursor);
    if (hit) {
      flushPlain();
      segments.push(...hit.segments);
      cursor += hit.length;
      continue;
    }

    plainBuffer += text[cursor];
    cursor += 1;
  }

  flushPlain();
  return segments;
}
